import classNames from 'classnames';
import { AriaRole, forwardRef, ReactNode, Ref } from 'react';
import type { ComponentProps } from 'react';
import Typography from '@alltrails/core/components/Typography';
import { InputBaseProps, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/InputField.module.scss';

function headingClass(variant: string) {
  switch (variant) {
    case 'heading100':
      return styles.heading100;
    case 'heading200':
      return styles.heading200;
    case 'heading300':
      return styles.heading300;
    case 'heading400':
      return styles.heading400;
    case 'heading500':
      return styles.heading500;
    case 'heading600':
      return styles.heading600;
    case 'heading700':
      return styles.heading700;
    default:
      return undefined;
  }
}

type InputFieldProps = Pick<InputBaseProps, 'disabled' | 'errorText' | 'helperText' | 'labelText' | 'labelTextVariant'> & {
  children: ReactNode;
  className?: string;
  characterLimitText?: ReactNode;
  'data-denali'?: string;
  labelTextColor?: ComponentProps<typeof Typography>['color'];
  role?: AriaRole;
  useDivInsteadOfLabel?: boolean;
  size?: Size<'sm' | 'md'>;
};

const InputField = forwardRef(
  (
    {
      characterLimitText,
      children,
      className,
      'data-denali': dataDenali,
      disabled,
      errorText,
      helperText,
      labelText,
      labelTextColor,
      labelTextVariant,
      role,
      useDivInsteadOfLabel,
      size = 'md'
    }: InputFieldProps,
    ref: Ref<HTMLElement>
  ) => {
    const resolvedLabelVariant = labelTextVariant ?? (size === 'sm' ? 'text100' : 'text200');
    const labelHeadingClass = headingClass(resolvedLabelVariant);
    const contents = (
      <>
        {labelText &&
          (labelHeadingClass ? (
            <div className={labelHeadingClass}>{labelText}</div>
          ) : (
            <Typography {...(labelTextColor ? { color: labelTextColor } : {})} component="div" variant={resolvedLabelVariant}>
              {labelText}
            </Typography>
          ))}
        {children}
        {!disabled && errorText && (
          <div className={styles.errorText}>
            <span>{errorText}</span> {characterLimitText && <span> {characterLimitText} </span>}
          </div>
        )}
        {helperText && <div className={styles.helperText}>{helperText}</div>}
      </>
    );
    return useDivInsteadOfLabel ? (
      <div
        className={classNames(styles.container, className)}
        {...setDenaliAttribute(dataDenali || 'InputField')}
        ref={ref as Ref<HTMLDivElement>}
        role={role}
      >
        {contents}
      </div>
    ) : (
      <label
        className={classNames(styles.container, className)}
        {...setDenaliAttribute(dataDenali || 'InputField')}
        ref={ref as Ref<HTMLLabelElement>}
        role={role}
      >
        {contents}
      </label>
    );
  }
);

export default InputField;
